import { createSelector } from '@reduxjs/toolkit'
import { all, call, put, select, StrictEffect } from 'redux-saga/effects'

import { DeleteOpeningApiResponse } from '../effects/handleDeleteDrawableGroup'
import { createHighlightFromLocation } from './markupDocument'
import {
    ADD_DRAWABLE_TO_ALL_GROUPS,
    setActiveDrawableGroup,
    UPDATE_OPENING_GROUPS_SUCCESS,
} from '../../../actions/drawable'
import { deleteOpenings } from '../../../api/projects-api'
import { bulkUpdateAutomatedObject } from '../../../api/takeoff-api'
import { ActiveFloor } from '../../../models/activeFloor'
import { AIAutomatedObject } from '../../../models/aiClassifications'
import { Project } from '../../../models/project'
import { RootState } from '../../../stores'
import { markAiSuggestionsDeleted } from '../../../utils/aiSuggestions/aiParser'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Color, Select, Workspace } from '../../lib/toolBoxes/2D'
import { Highlight } from '../../lib/toolBoxes/2D/tools/highlight/Highlight.tool'
import {
    deleteOpeningLocation,
    selectAiSuggestions,
    setActiveHighlightId,
    setActiveHighlightOpeningId,
    setAiSuggestions,
    setDrawableLocations,
    updateSelectedPaperId,
} from '../../slices/2D'
import { setHighlightPopupColor } from '../../slices/common'
import {
    ActiveGeometryGroup,
    GeometricDrawable,
    GeometryGroup,
    selectActiveGeometryGroup,
    selectAllGeometricDrawables,
    selectDrawableGeometries,
    selectDrawableGroupsGeometries,
} from '../../slices/geometry'
import { selectProject } from '../../slices/project'
import { IMUP2DDrawableLocation } from '../../types'

export const selectBaseDrawableState = ({ ignoreHidden = false }: { ignoreHidden: boolean }) =>
    createSelector([(state: RootState) => state], (state) => ({
        drawables: selectDrawableGeometries(state, ignoreHidden),
        drawableLocations: state.IMUP['2D'].drawableLocations,
    }))

export function* deleteHighlightLocation(
    highlightTool: Highlight,
    colorTool: Color,
    selectTool: Select,
    currentDrawableLocations: IMUP2DDrawableLocation[],
    project: Project,
    items: paper.Item[]
) {
    const highlightDrawableLocations: IMUP2DDrawableLocation[] | undefined = yield currentDrawableLocations.filter(
        (location) => items.some((item) => item.data.drawable_id === location.drawable_id)
    )

    if (!highlightDrawableLocations) return

    // remove active highlight id to toggle clean up of selectedItem in Select.tool.ts
    yield put(setActiveHighlightId(null))

    yield put(setActiveHighlightOpeningId(null))

    yield put(setHighlightPopupColor(null))

    yield all(
        highlightDrawableLocations.map((location) => {
            return put(deleteOpeningLocation(location.opening_location_id))
        })
    )

    yield put(updateSelectedPaperId(null))

    yield all(items.map((item) => call(item.remove.bind(item))))

    yield call(selectTool.removeHandles)

    try {
        const drawableIds: number[] = items.map((item) => item.data.drawable_id)

        const res: DeleteOpeningApiResponse = yield call(deleteOpenings, project.id, drawableIds)

        if (res.errors?.length > 0) {
            throw new Error(
                `Deletion was unsuccessful: ${res.errors
                    .map((e) => `Error: ${e.error}, OpeningId: ${e.opening_id}`)
                    .join('. ')}`
            )
        }
    } catch (e) {
        yield call(console.error, `Error on API deletion: ${e}`)

        yield all(
            highlightDrawableLocations.map((location) =>
                call(createHighlightFromLocation, highlightTool, colorTool, location)
            )
        )

        // Reset the drawable locations in the store to
        // add back the deleted highlight
        yield put(setDrawableLocations(currentDrawableLocations))
    }
}

export function* deleteAiSuggestion(aiSuggestionId: string[]) {
    try {
        const aiDataArray: AIAutomatedObject[] = yield select(selectAiSuggestions)
        const updatedAiData: AIAutomatedObject[] = yield markAiSuggestionsDeleted(aiDataArray, aiSuggestionId)

        const project: Project = yield select(selectProject)

        yield call(bulkUpdateAutomatedObject, project.id, aiSuggestionId)

        // Update Ai in the store
        yield put(setAiSuggestions(updatedAiData))
    } catch (e) {
        console.error('Error on deleting updating AI', e)
    }
}

const getGroupsToProcess = (relevantGroups: GeometryGroup[], activeDrawableGroup: ActiveGeometryGroup) => {
    if (relevantGroups.length > 0) {
        return relevantGroups
    }

    if (activeDrawableGroup) {
        return [activeDrawableGroup]
    }

    return []
}

/**
 * Deletes the active drawable
 *  - Takes an optimistic approach to deletion: removes the paper item -> removes it from the store -> sends api call -> on failure resets the store to pre-delete state
 */
export function* deleteDrawableLocation2D({
    payload,
}: {
    payload: number[]
}): Generator<StrictEffect, void, PaperManager & Highlight & Workspace & ActiveGeometryGroup & ActiveFloor & Project> {
    try {
        const openingIdsSet = new Set(payload)
        const manager: PaperManager = yield call(managers.get2DManager)

        if (!manager) return

        const [workspaceTool, selectTool]: [Workspace, Select] = yield call(manager.getTools, [
            Workspace.NAME,
            Select.NAME,
        ])

        const project: Project = yield select(selectProject)

        const {
            drawableLocations,
            drawables,
        }: {
            drawableLocations: IMUP2DDrawableLocation[]
            drawables: GeometricDrawable[]
        } = yield select(selectBaseDrawableState({ ignoreHidden: true }))

        const checkShownDrawables = drawables.filter((d) => openingIdsSet.has(d.id))
        const allDrawablesFallback: GeometricDrawable[] = checkShownDrawables.length
            ? []
            : yield select(selectAllGeometricDrawables)
        const currentDrawables = checkShownDrawables.length
            ? checkShownDrawables
            : allDrawablesFallback.filter((d) => openingIdsSet.has(d.id))

        if (!currentDrawables.length) {
            return
        }
        const activeDrawableGroup: ActiveGeometryGroup = yield select(selectActiveGeometryGroup)
        const itemsToDelete: paper.Item[] = yield call(workspaceTool.getItemsWithCriteria, 'data', (data) =>
            openingIdsSet.has(data.drawable_id)
        )

        const drawableGroups: GeometryGroup[] = yield select(selectDrawableGroupsGeometries)

        // get all relevant groups for the current drawables
        const relevantGroups = drawableGroups.filter((drawableGroup) =>
            currentDrawables.some((drawable) => drawable.opening_group_id === drawableGroup.id)
        )

        // if we have an active drawable group that matches, include it (handles single group case)
        const groupsToProcess = getGroupsToProcess(relevantGroups, activeDrawableGroup)

        // process each group to ensure drawables are populated and create updates
        const groupsToUpdate = groupsToProcess.map((group) => {
            const processedGroup = !group?.drawables?.length
                ? {
                      ...group,
                      drawables: group.openings || [],
                  }
                : group

            const newGroup: ActiveGeometryGroup = {
                ...processedGroup,
                openings: processedGroup.openings.filter((opening) => !openingIdsSet.has(opening.id)),
                drawables: processedGroup.drawables.filter((drawable) => !openingIdsSet.has(drawable.id)),
            }

            return {
                originalGroup: processedGroup,
                newGroup: newGroup,
            }
        })

        // update all affected groups
        yield all([
            ...groupsToUpdate.map((update) =>
                put({
                    type: UPDATE_OPENING_GROUPS_SUCCESS,
                    payload: { openingGroups: update },
                })
            ),
            ...itemsToDelete.map((i) => call(i.remove.bind(i))),
            call(selectTool.removeHandles),
        ])

        // check if any groups become empty after deletion
        const allGroupsEmpty = groupsToUpdate.every((update) => update.newGroup.openings.length === 0)

        if (allGroupsEmpty) {
            yield put(setActiveDrawableGroup(null))
        }

        // try the API call. if there's an error -> undo the deletion actions to reset the drawable & paper state
        try {
            const res: DeleteOpeningApiResponse = yield call(deleteOpenings, project.id, payload)

            if (res.errors?.length > 0) {
                throw new Error(
                    `Deletion was unsuccessful: ${res.errors
                        .map((e) => `Error: ${e.error}, OpeningId: ${e.opening_id}`)
                        .join('. ')}`
                )
            }
        } catch (e) {
            yield call(console.error, `Error on API drawable location deletion: ${e}`)

            // redraw the item on the blueprint
            yield all(itemsToDelete.map((i) => call(workspaceTool.addPaperItem, i)))

            // restore all groups to their original state
            yield all([
                ...groupsToUpdate.map((update) =>
                    put({
                        type: UPDATE_OPENING_GROUPS_SUCCESS,
                        payload: {
                            openingGroups: {
                                newGroup: update.originalGroup,
                                originalGroup: update.originalGroup,
                            },
                        },
                    })
                ),
                ...currentDrawables.map((currentDrawable) =>
                    put({
                        type: ADD_DRAWABLE_TO_ALL_GROUPS,
                        payload: currentDrawable,
                    })
                ),
                put({
                    type: setDrawableLocations.type,
                    payload: drawableLocations,
                }),
            ])
        }

        // Delete ai suggestion
        for (const currentDrawable of currentDrawables) {
            if (currentDrawable?.ai_suggestion_id) {
                yield call(deleteAiSuggestion, [currentDrawable.ai_suggestion_id])
            }
        }
    } catch (error) {
        yield call(console.error, error as any)
    }
}
